import { gql } from '@apollo/client';

/**
 * International device orders — the International Requests tab.
 *
 * Each row is a request a STAFF REP filed on a customer's behalf. There is no poll and no public
 * form: the demand arrives through a sales conversation with a prospect who usually has no
 * OptiSigns account, so this is the one device ledger in the app whose rows are typed in by us.
 *
 * Operation names match their GraphQL field names so `refetchQueries: ['intlRequests']` resolves.
 *
 * THE INVOICE OPERATIONS ARE HERE. `raiseIntlInvoice` is the one operation in this file that
 * moves money: it creates a Stripe DRAFT and arms Stripe's own auto-send window (1h for a
 * positively-identified human), so nothing here emails a customer synchronously. It refuses a
 * second invoice on a request that already has one.
 *
 * There is still no POLLER — payment is learned by asking, through `refreshIntlInvoice` (one row)
 * or `refreshIntlInvoices` (every live invoice). The automatic half arrives with the fulfilment
 * lane.
 */

const INTL_REQUEST_FIELDS = `
  id
  source
  submittedBy
  submittedAt
  stripeCustomerId
  contactEmail
  contactName
  company
  vatNumber
  accountId
  hubspotContactId
  hubspotDealId
  shipToName
  shipToLine1
  shipToLine2
  shipToCity
  shipToRegion
  shipToPostalCode
  shipToCountry
  shipToPhone
  addressComplete
  addressMissing
  deviceModel
  quantity
  currency
  lastUpdatedBy
  lastUpdatedAt
  stripeInvoiceId
  invoiceNumber
  invoiceStatus
  invoiceAutoSendAt
  invoicePaidAt
  invoiceCheckedAt
  invoiceRaisedBy
  invoiceRaisedAt
  invoiceError
  invoiceUrl
  mcfOrderId
  mcfMarketplace
  mcfSellerSku
  mcfShippingSpeed
  mcfAttempt
  mcfSubmittedAt
  mcfSubmittedBy
  mcfReleasedAt
  mcfReleasedBy
  mcfCancelledAt
  mcfCancelledBy
  mcfStatus
  mcfStatusAt
  mcfError
  mcfHeld
  mcfUnpaidApprovedAt
  mcfUnpaidApprovedBy
  mcfUnpaidReason
  accountDeactivatedAt
  accountDeactivatedBy
  accountDeactivatedReason
  deactivatedAccountId
  mcfQuote { speed fulfillable feeMinor currency earliestShip latestShip earliestArrival latestArrival }
  mcfQuotedAt
  mcfFeeMinor
  mcfFeeCurrency
  invoiceFreightMinor
  poNumber
  poDocumentId
  stripeQuoteId
  quoteStatus
  quoteNumber
  quoteUnitAmount
  quoteShippingAmount
  quoteTotal
  quoteCurrency
  quoteExpiresAt
  quoteQuantity
  quoteShipToCountry
  quoteRevision
  quoteCreatedBy
  quoteCreatedAt
  quoteAcceptedBy
  quoteAcceptedAt
  quoteError
  quoteAmazonFeeMinor
  quoteAmazonFeeCurrency
  quoteAmazonSpeed
  quoteAmazonQuotedAt
  chinaTrackingNumber
  chinaCarrier
  chinaFulfilledAt
  chinaFulfilledBy
  mcfPackages {
    trackingNumber
    carrierCode
    estimatedArrivalDate
    packageNumber
    amazonShipmentId
    fulfillmentCenterId
    shipmentStatus
  }
  stage
`;

/**
 * The work list, newest-filed first.
 *
 * `filter.mine` is resolved SERVER-SIDE from the caller's token — there is deliberately no
 * `submittedBy` filter, so holding the tab does not let anyone enumerate a colleague's pipeline.
 * `filter.stage` matches the same stamps the `stage` field is derived from, so the filter and the
 * column cannot disagree. `filter.search` accepts `IR-62`, `ir 62`, `#62` or `62` for an exact
 * row, and otherwise spans company, contact, the Stripe customer id and the Stripe invoice id —
 * that last one because it is the only value an operator arrives holding from another system.
 */
export const INTL_REQUESTS = gql`
  query intlRequests($filter: IntlRequestFilterInput, $pagination: PaginationInput) {
    intlRequests(filter: $filter, pagination: $pagination) {
      items { ${INTL_REQUEST_FIELDS} }
      total
    }
  }
`;

export const INTL_REQUEST = gql`
  query intlRequest($requestId: Int!) {
    intlRequest(requestId: $requestId) { ${INTL_REQUEST_FIELDS} }
  }
`;

export const INTL_REQUEST_NOTES = gql`
  query intlRequestNotes($requestId: Int!) {
    intlRequestNotes(requestId: $requestId) {
      id
      staffEmail
      text
      createdAt
    }
  }
`;

/**
 * File a request. root + support only — filing is a sales act, and the submitter is taken from
 * the auth token rather than the input so the record of who is accountable cannot be typed in.
 *
 * The server refuses per field and by name: a malformed `cus_…`, a country that is not an ISO
 * alpha-2 code (`UK` gets told to use `GB`), an unknown model, a quantity outside 1..9999, and a
 * contact address that is not an email.
 */
export const SUBMIT_INTL_REQUEST = gql`
  mutation submitIntlRequest($input: SubmitIntlRequestInput!) {
    submitIntlRequest(input: $input) { ${INTL_REQUEST_FIELDS} }
  }
`;

/**
 * Correct a filed request — most often to complete the shipping address, which a rep filing from
 * a call usually does not have.
 *
 * Ship-to COUNTRY and DEVICE MODEL are not in the input and cannot be changed: together they are
 * the request's identity for tax, legality, sourcing and freight. Omitting a field LEAVES IT
 * ALONE; there is no way to clear one back to empty.
 */
export const UPDATE_INTL_REQUEST = gql`
  mutation updateIntlRequest($input: UpdateIntlRequestInput!) {
    updateIntlRequest(input: $input) { ${INTL_REQUEST_FIELDS} }
  }
`;

export const ADD_INTL_REQUEST_NOTE = gql`
  mutation addIntlRequestNote($requestId: Int!, $text: String!) {
    addIntlRequestNote(requestId: $requestId, text: $text) {
      id
      staffEmail
      text
      createdAt
    }
  }
`;

/**
 * Raise the DRAFT invoice, and arm its 1-hour auto-send.
 *
 * root + support only — the same tier as filing, because raising the invoice is the other half
 * of the sales act rather than a fulfilment step.
 *
 * `unitAmount` and `shippingAmount` are MINOR UNITS (79900 is $799.00). The product and the
 * quantity are NOT sent: both come from the request server-side, so an invoice cannot be raised
 * for hardware the customer did not ask for.
 *
 * The returned row carries `invoiceAutoSendAt` when Stripe accepted the deadline. When it did
 * not, that is reported rather than thrown — the invoice exists and simply waits for a person.
 */
export const RAISE_INTL_INVOICE = gql`
  mutation raiseIntlInvoice($input: RaiseIntlInvoiceInput!) {
    raiseIntlInvoice(input: $input) { ${INTL_REQUEST_FIELDS} }
  }
`;

/** Re-read ONE request's invoice from Stripe. The wider tier: asking costs nothing. */
export const REFRESH_INTL_INVOICE = gql`
  mutation refreshIntlInvoice($requestId: Int!) {
    refreshIntlInvoice(requestId: $requestId) { ${INTL_REQUEST_FIELDS} }
  }
`;

/**
 * Re-read EVERY live invoice from Stripe, and report what moved.
 *
 * Skips rows already paid — a paid invoice does not go backwards. Returns counts rather than
 * rows, because "checked but nothing moved" and "nothing to check" are indistinguishable
 * without them.
 */
export const REFRESH_INTL_INVOICES = gql`
  mutation refreshIntlInvoices {
    refreshIntlInvoices {
      checked
      changed
      failed
    }
  }
`;

/**
 * The Amazon half of the one Refresh button — every live fulfilment order, re-read.
 *
 * WIDER than the hourly poller's selection: it includes HELD orders and ones that already have
 * tracking, because a person pressing Refresh is asking for everything to be current, not just
 * for a missing tracking number. Terminally dead orders are still skipped.
 *
 * Its own mutation rather than folded into the invoice sweep: one button, two honest answers, so
 * a Stripe outage still lets Amazon's half report.
 */
export const REFRESH_INTL_FULFILMENT = gql`
  mutation refreshIntlFulfilment {
    refreshIntlFulfilment {
      checked
      changed
      failed
    }
  }
`;

/**
 * Who is `cus_…`? Read-only, and what makes the intake form prefill itself.
 *
 * The form calls this as the rep finishes typing an id and fills in company, contact email,
 * contact name and VAT number from what Stripe holds. A DELETED customer comes back with
 * `deleted: true` rather than as an error — the fix is a different customer, not a retry.
 *
 * NOTE THE ASYMMETRY this half of the pair implies: the form READS all four, and TWO things are
 * written BACK to Stripe — the VAT number (on submit, and on a correction that changes it) and,
 * since 2026-09-18, the ship-to address, onto `customer.address`. Contact name and email are
 * per-request facts and must not overwrite the billing contact.
 *
 * `billingAddress` IS NOT A PREFILL SOURCE. It comes back so the form can show the rep the
 * address that filing is about to replace — the ship-to block is a destination and must never be
 * seeded from a billing record.
 */
export const INTL_STRIPE_CUSTOMER = gql`
  query intlStripeCustomer($stripeCustomerId: String!) {
    intlStripeCustomer(stripeCustomerId: $stripeCustomerId) {
      id
      deleted
      name
      email
      phone
      contactName
      vatNumber
      currency
      billingAddress {
        line1
        line2
        city
        state
        postalCode
        country
      }
    }
  }
`;

/**
 * ── Amazon MCF: the fulfilment half ────────────────────────────────────────────────────────────
 *
 * root + FULFILLMENT only for the three that move goods — narrower than the invoice mutations
 * beside them, and narrow in the other direction: support may bill a customer but not ship to
 * one. The refresh is on the wider tier, because asking Amazon costs nothing.
 */

/**
 * Submit a paid request to Amazon as a HELD fulfilment order.
 *
 * Amazon accepts it — real SKU, real stock, real address — and ships NOTHING until
 * `releaseIntlMcfOrder`. Every refusal is named; the ones that are Amazon's or the SKU
 * registry's are also written to `mcfError` so the queue shows why a paid request has not moved.
 */
export const SUBMIT_INTL_MCF_ORDER = gql`
  mutation submitIntlMcfOrder($requestId: Int!) {
    submitIntlMcfOrder(requestId: $requestId) { ${INTL_REQUEST_FIELDS} }
  }
`;

/** Release a held order to ship. The one act in this feature that cannot be taken back. */
export const RELEASE_INTL_MCF_ORDER = gql`
  mutation releaseIntlMcfOrder($requestId: Int!) {
    releaseIntlMcfOrder(requestId: $requestId) { ${INTL_REQUEST_FIELDS} }
  }
`;

/**
 * Record that a request shipped from CHINA, with a hand-entered tracking number.
 *
 * The second fulfilment route (2026-09-17). For the four remote EU destinations the panel already
 * suggested China Fulfilled over Amazon MCF; this is what lets the outcome be recorded rather
 * than leaving the request stuck at "paid, not fulfilled" while the goods are on a plane.
 *
 * It reaches the same FULFILLED stages an Amazon tracking number does, and takes the request off
 * the Amazon lane so the twice-daily poll cannot place a duplicate held order.
 */
export const FULFIL_INTL_VIA_CHINA = gql`
  mutation fulfilIntlViaChina($requestId: Int!, $input: IntlChinaFulfilInput!) {
    fulfilIntlViaChina(requestId: $requestId, input: $input) { ${INTL_REQUEST_FIELDS} }
  }
`;

/**
 * Cancel a fulfilment order — and the way a wrong shipping address gets fixed: cancel, correct,
 * submit again. The retry gets a fresh order id, because Amazon never reuses one.
 */
export const CANCEL_INTL_MCF_ORDER = gql`
  mutation cancelIntlMcfOrder($requestId: Int!) {
    cancelIntlMcfOrder(requestId: $requestId) { ${INTL_REQUEST_FIELDS} }
  }
`;

/** Ask Amazon for one order's status and tracking. A read; the wider role tier. */
export const REFRESH_INTL_MCF_ORDER = gql`
  mutation refreshIntlMcfOrder($requestId: Int!) {
    refreshIntlMcfOrder(requestId: $requestId) { ${INTL_REQUEST_FIELDS} }
  }
`;

/**
 * Amazon's own answer about a fulfilment order, verbatim, as text.
 *
 * A String rather than a JSON scalar: it is only ever displayed, and a `GraphQLJSON` would put
 * an untyped hole in the schema for the privilege. Fetched on demand — nothing shows it until
 * somebody asks, because it is a debugging window rather than part of the record.
 */
export const INTL_MCF_RAW_ORDER = gql`
  query intlMcfRawOrder($requestId: Int!) {
    intlMcfRawOrder(requestId: $requestId)
  }
`;

/**
 * May the caller use International Requests at all?
 *
 * The ROLLOUT gate, which is a separate axis from the role tiers: while it is on, the feature is
 * root-only whatever a caller's roles say. EXEMPT from that gate server-side — a support user has
 * to be able to ask and be told no, rather than have the question refused.
 *
 * `rootOnly` is carried alongside `visible` so the console can say "not open yet" instead of
 * simply omitting a tab, which reads as a bug to whoever was told to expect it.
 */
export const INTL_REQUESTS_ACCESS = gql`
  query intlRequestsAccess {
    intlRequestsAccess {
      visible
      rootOnly
    }
  }
`;

/**
 * ── The export ─────────────────────────────────────────────────────────────────────────────────
 *
 * root + FULFILLMENT only — narrower than the read tier, because a file of customer names,
 * addresses and phone numbers leaving the building is a different act from reading a row. Built
 * on the SERVER for that reason: if the console assembled it from a list query, any support user
 * could reconstruct it by hand.
 */

/** The columns the export can carry, so the picker holds no copy of the server's registry. */
export const INTL_EXPORT_COLUMNS = gql`
  query intlExportColumns {
    intlExportColumns {
      key
      label
      default
    }
  }
`;

/**
 * The CSV, as text. `from`/`to` are inclusive `YYYY-MM-DD` bounds on the FILED date, read in
 * Central time so the range matches the dates the queue displays.
 */
export const INTL_REQUESTS_EXPORT = gql`
  query intlRequestsExport($input: IntlRequestExportInput) {
    intlRequestsExport(input: $input)
  }
`;

/**
 * ── The two ROOT-ONLY overrides ────────────────────────────────────────────────────────────────
 *
 * Narrower than every other tier here, fulfilment included: shipping before the money arrives
 * and switching a customer's screens off are commercial calls, not operational ones. Both take a
 * mandatory reason and both are stamped with the caller from the token.
 */

/**
 * Approve shipping an OPEN invoice — submits AND releases in one act.
 *
 * One mutation because it is one decision: the Hold between submit and release exists to stop an
 * unattended machine shipping something, and protects against nothing once a human has decided
 * to ship it. Only the payment check is waived; address, destination, SKU and Amazon's own
 * preview all still have to pass.
 */
export const APPROVE_INTL_UNPAID_SHIPMENT = gql`
  mutation approveIntlUnpaidShipment($requestId: Int!, $reason: String!) {
    approveIntlUnpaidShipment(requestId: $requestId, reason: $reason) { ${INTL_REQUEST_FIELDS} }
  }
`;

/**
 * Switch the customer's OptiSigns account off over an unpaid shipment.
 *
 * Requires that the goods actually shipped and that the invoice is still outstanding. The write
 * borrows the same path the Devices Order chase uses, and there is deliberately NO undo here —
 * re-enabling is a billing decision taken when payment arrives.
 */
export const DEACTIVATE_INTL_CUSTOMER_ACCOUNT = gql`
  mutation deactivateIntlCustomerAccount($requestId: Int!, $reason: String!) {
    deactivateIntlCustomerAccount(requestId: $requestId, reason: $reason) { ${INTL_REQUEST_FIELDS} }
  }
`;

/**
 * Which device models this lane may be used for — the Amazon MCF allowlist.
 *
 * Root ticks "Amazon MCF" per product on `/staff/optisigns-products`; this is what the intake
 * form's item picker is built from, so a device that is not stocked in an Amazon fulfilment
 * centre is not offerable. An EMPTY list is a real answer (nothing stocked, or the product
 * registry unreachable) and the form says so rather than showing an empty dropdown.
 */
export const INTL_MCF_ELIGIBLE_MODELS = gql`
  query intlMcfEligibleModels {
    intlMcfEligibleModels
  }
`;

/**
 * WHAT A REQUEST WOULD COST, priced and committed to nothing.
 *
 * The summary the intake form shows after filing, before the rep chooses a quote or an invoice.
 * A QUERY, because it creates no Stripe object and writes no row — that is the whole reason it
 * exists rather than the rep having to raise something to find out.
 */
/**
 * What Amazon would charge to ship it. A SEPARATE query from `INTL_REQUEST_PRICING` on purpose:
 * pricing is arithmetic and always answers, this crosses the network and often does not, and the
 * summary card must still render its total when Amazon is unreachable.
 */
export const INTL_REQUEST_FREIGHT_QUOTE = gql`
  query IntlRequestFreightQuote($requestId: Int!) {
    intlRequestFreightQuote(requestId: $requestId) {
      speeds {
        speed
        fulfillable
        feeMinor
        currency
        latestArrival
      }
      unavailableReason
      marketplace
      sellerSku
    }
  }
`;

export const INTL_REQUEST_PRICING = gql`
  query IntlRequestPricing($requestId: Int!) {
    intlRequestPricing(requestId: $requestId) {
      unitAmount
      shippingAmount
      freightUnavailableReason
      freightCostMinor
      freightSpeed
      freightFxNote
      total
      currency
      sku
      quantity
    }
  }
`;

/** Raise the quote — create it in Stripe, finalize it, and record what the customer is shown. */
export const CREATE_INTL_QUOTE = gql`
  mutation CreateIntlQuote($input: CreateIntlQuoteInput!) {
    createIntlQuote(input: $input) { ${INTL_REQUEST_FIELDS} }
  }
`;

/** Re-price: cancel the standing quote and raise a replacement. `supersedeReason` is required. */
export const SUPERSEDE_INTL_QUOTE = gql`
  mutation SupersedeIntlQuote($input: CreateIntlQuoteInput!) {
    supersedeIntlQuote(input: $input) { ${INTL_REQUEST_FIELDS} }
  }
`;

/** Withdraw the quote. The request drops back to Filed. */
export const CANCEL_INTL_QUOTE = gql`
  mutation CancelIntlQuote($requestId: Int!) {
    cancelIntlQuote(requestId: $requestId) { ${INTL_REQUEST_FIELDS} }
  }
`;

/**
 * CONVERT: accept the quote and bill what it promised.
 *
 * The op that moves money on this path. It is refused for an expired quote, and for a STALE one
 * — where the request's quantity or destination changed after it was priced.
 */
export const CONVERT_INTL_QUOTE_TO_INVOICE = gql`
  mutation ConvertIntlQuoteToInvoice($input: ConvertIntlQuoteInput!) {
    convertIntlQuoteToInvoice(input: $input) { ${INTL_REQUEST_FIELDS} }
  }
`;

/** Re-read the quote from Stripe — it can be accepted or expire without telling us. */
export const REFRESH_INTL_QUOTE = gql`
  mutation RefreshIntlQuote($requestId: Int!) {
    refreshIntlQuote(requestId: $requestId) { ${INTL_REQUEST_FIELDS} }
  }
`;
